import { useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { noop } from 'underscore';
import { startAssetEdit, stopAssetEdit } from 'redux/modules/embed/actions';
import { ArgsOf } from 'types';
import { ResizeCallback } from './TrackAssetDragHandles';

export type TrackAssetResizeStopCallback = (
  resized: boolean,
  ...args: ArgsOf<ResizeCallback>
) => void;

export type UseResizeTrackAssetProps = {
  assetId: string;
  onResize?: ResizeCallback;
  onResizeStart?: ResizeCallback;
  onResizeStop?: TrackAssetResizeStopCallback;
};

export default function useResizeTrackAsset(props: UseResizeTrackAssetProps) {
  const {
    assetId,
    onResize = noop,
    onResizeStart = noop,
    onResizeStop = noop,
  } = props;
  const dispatch = useDispatch();

  const [resizing, setResizing] = useState(false);

  const resized = useRef(false);

  const handleResize: ResizeCallback = useCallback(
    (...args) => {
      onResize(...args);
      resized.current = true;
    },
    [onResize],
  );

  const handleResizeStart: ResizeCallback = useCallback(
    (...args) => {
      dispatch(startAssetEdit(assetId, 'resize'));
      resized.current = false;
      setResizing(true);
      onResizeStart(...args);
    },
    [assetId, dispatch, onResizeStart],
  );

  const handleResizeStop: ResizeCallback = useCallback(
    (...args) => {
      dispatch(stopAssetEdit());
      setResizing(false);
      onResizeStop(resized.current, ...args);
    },
    [dispatch, onResizeStop],
  );

  return {
    resizing,
    handleResize,
    handleResizeStart,
    handleResizeStop,
  };
}
